/**
 * Copy the opener's stylesheets into a pop-out window.
 *
 * Prefers inlining `cssRules` as `<style>` elements so that styles work in
 * `about:blank` windows where `'self'` resolves to the null origin and
 * external stylesheet URLs would be blocked by CSP `style-src-elem`.
 *
 * Falls back to `<link>` elements when `cssRules` cannot be read (cross-origin
 * `SecurityError`) or when the stylesheet has no rules yet (still loading).
 *
 * @example
 * ```ts
 * const popOut = window.open('', '');
 * if (popOut) {
 *     copyStyles(document, popOut);
 * }
 * ```
 *
 * @param source - The document to read stylesheets from.
 * @param target - The pop-out window to copy styles into.
 */
export const copyStyles = (source: Document, target: Window) => {
    const stylesheets = Array.from(source.styleSheets);
    for (const stylesheet of stylesheets) {
        try {
            if (stylesheet.cssRules && stylesheet.cssRules.length > 0) {
                const newStyleElement = target.document.createElement('style');
                for (const rule of Array.from(stylesheet.cssRules)) {
                    newStyleElement.appendChild(
                        target.document.createTextNode(rule.cssText),
                    );
                }
                target.document.head.appendChild(newStyleElement);
            } else if (stylesheet.href) {
                const newStyleElement = target.document.createElement('link');
                newStyleElement.rel = 'stylesheet';
                newStyleElement.href = stylesheet.href;
                target.document.head.appendChild(newStyleElement);
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === 'SecurityError') {
                if (stylesheet.href) {
                    const newStyleElement =
                        target.document.createElement('link');
                    newStyleElement.rel = 'stylesheet';
                    newStyleElement.href = stylesheet.href;
                    target.document.head.appendChild(newStyleElement);
                }
            } else {
                throw e;
            }
        }
    }
};
